import * as React from "react";
import { Route, RouterProps, RouteProps } from "@core-ui/interfaces/Router";

// TODO: add a check for unique route name..
export function buildRoutes<T extends string>(childrenArray: RouterProps<T>["children"], parent?: T): Route<T>[] {
    const routes: Route<T>[] = [];

    React.Children.forEach(childrenArray, children => {
        if (!React.isValidElement(children)) return;

        const childrenProps = children.props;
        routes.push({
            ...childrenProps,
            parent,
            element: children,
        });

        // eslint-disable-next-line no-nested-ternary
        const childrens = Array.isArray(childrenProps.children)
            ? childrenProps.children
            : childrenProps.children
            ? [childrenProps.children]
            : [];

        // child routes
        routes.push(...buildRoutes(childrens, childrenProps.name));
    });

    return routes;
}

export function findRouteByName<T extends string>(
    routes: Route<T>[],
    name: T,
    activeRouteName?: T,
): Route<T> | undefined {
    const matchResults = routes.filter(route => route.name === name);
    //handle collision
    if (matchResults.length > 1 && !!activeRouteName) {
        return matchResults.find(route => route.parent === activeRouteName) ?? matchResults[0];
    }
    return matchResults[0];
}

function cloneRouteElement<T extends string>(route: Route<T>): React.ReactElement<RouteProps<T>> {
    return React.cloneElement(
        route.element,
        { key: route.name, data: route.data },
        route.indexElement != null
            ? [React.cloneElement(route.indexElement, { key: route.indexElement.props.name, data: route.data })]
            : [],
    );
}

export function getRouteElement<T extends string>(routes: Route<T>[], route?: Route<T>): React.ReactElement | null {
    if (!route) return null;

    let element = cloneRouteElement(route);
    let _route = route.parent ? findRouteByName(routes, route.parent) : null;

    while (_route != null) {
        element = React.cloneElement(_route.element, { key: _route.name }, [element]);
        _route = _route.parent ? findRouteByName(routes, _route.parent) : null;
    }

    return element;
}

export type RouteStack<T extends string> = Array<{
    name: T;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
}>;

export function addToStack<T extends string>(opts: {
    stack: RouteStack<T>;
    name: T;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any;
    replace?: boolean;
}): [RouteStack<T>, boolean] {
    const newEntry = { name: opts.name, data: opts.data };
    const existingPosition = opts.stack.findIndex(s => s.name === opts.name);

    if (existingPosition === -1) {
        if (opts.replace && opts.stack.length > 0) {
            const stack = [...opts.stack];
            stack[stack.length - 1] = newEntry;
            history.replaceState(newEntry, "", "");
            return [stack, false];
        }

        history.pushState(newEntry, "", "");
        return [[...opts.stack, newEntry], true];
    }

    const newStack = opts.stack.slice(0, existingPosition + 1);
    newStack[newStack.length - 1] = newEntry;
    return [newStack, false];
}
